/*
 * The code not covered is related to the behavior of the element in the browser.
 * Jest unfortunately cannot simulate interface behaviours, so these will have
 * integration tests.
 */

import * as React from 'react'
import * as classNames from 'classnames'
import * as style from './SuperHero.module.scss'
import * as csm from '../../../utils/csm'
import { HeroElement } from './HeroElement'
import DotNav from './DotNav'
import DraperPlayer from '../../../../../DVWebNodeDraperPlayer/DraperPlayer'
import { StorefrontStateCollection, FeatureSwitches } from '../../../containers/Storefront';
import { StorefrontItem, WPSClient, withArgs, Args } from '@utils'
import { v2Request } from '../../../../../utils/EnrichItemMetadataUtils'

const NO_ACTIVE_INDEX = -1
const PREROLL_ANIMATION_DELAY = 1000
const PREROLL_CONTINUE_SLIDER_DELAY = 2000
const AUTOSCROLL_DELAY = 5000

declare let window: Window & {
  P: any
}

enum Direction {
  BACKWARD = -1,
  FORWARD = 1
}

const realMod = (n: number, m: number) => {
  return ((n % m) + m) % m;
}

interface SuperHeroProps extends StorefrontStateCollection {
  isFrozen?: boolean
  draperPlayerComponent?: DraperPlayer
  formFactor: string
  featureSwitches?: FeatureSwitches
}

interface SuperHeroState {
  loopCompleted: boolean
  index: number
  scrollLeft: number
  hasInteracted: boolean
  isJavascriptEnabled: boolean
  pausedForPlayback: boolean
  enrichments: { [name: string]: StorefrontItem }
  hasError: boolean
}

class SuperHeroComponent extends React.Component<SuperHeroProps & Args, SuperHeroState> {

  intervalID?: number
  carouselElement?: HTMLUListElement
  carouselContainer?: HTMLDivElement
  rightButton?: HTMLButtonElement
  heroComponents: { [x: string]: HeroElement; } = {}
  draperPlayerComponent?: DraperPlayer
  resizeListener?: EventListener
  touchXStart = 0

  constructor(props: SuperHeroProps & Args) {
    super(props)
    this.state = {
      index: 0,
      scrollLeft: 0,
      hasInteracted: false,
      isJavascriptEnabled: false,
      loopCompleted: false,
      pausedForPlayback: false,
      enrichments: {},
      hasError: false
    }
  }


  recalculateState(index: number) {
    const scrollLeft = (this.carouselElement && this.carouselElement.scrollLeft) || 0
    return {
      index: typeof index === 'number' ? index : this.state.index,
      scrollLeft,
      isJavascriptEnabled: true,
      loopCompleted: this.state.loopCompleted
    }
  }

  addPlayerListeners() {
    if (!global.__NATIVEAPP__ && typeof window !== 'undefined' && window.P) {
      window.P.when('dv-player-integration').execute((player: any) => {
        player.runBeforePlay(() => {
          if (!this.shouldNotAutoscroll()) {
            this.setState({ pausedForPlayback: true })
          }
        })
        player.on('closed', () => {
          this.setState({ pausedForPlayback: false }, () => {
            if (!this.shouldNotAutoscroll()) {
              this.moveTo(this.state.index + 1)
              this.autoScrollElements()
            }
          })
        })
      })
    }
  }

  componentDidMount() {
    if (!this.carouselElement || !this.carouselElement.children ||
      !this.carouselElement.children.length) {
      return
    }
    this.moveTo(0)
    this.setState(this.recalculateState(this.state.index))
    this.initResizeAndTouchEvents()
    this.initTabEvents()
    if (this.canPlayPreroll() === true) {
      this.playPreroll()
    }
    this.addPlayerListeners()
    this.enrichItems()
  }

  enrichItems() {
    if (!(this.props.featureSwitches && this.props.featureSwitches.ajaxSuperhero))
      return
    const titleIDs = this.props.items.map(item => item.titleID).filter(Boolean) as string[]
    const wps = new WPSClient()
    const request = v2Request(titleIDs, "SUPERHERO", true, true, false, true)
    if (titleIDs) {
      wps.EnrichItemMetadata({
        ...request,
        context: this.props.context
      }).then((r) => this.setState({ enrichments: r.enrichments }))
    }
  }

  componentWillUnmount() {
    this.removeResizeAndTouchEvents()
    this.removeTabEvents()
    if (this.intervalID) {
      this.resetInterval()
    }
    if (this.resizeListener) {
      window.removeEventListener('resize', this.resizeListener)
    }
  }

  componentDidCatch(error: Error) {
    csm.count('DVWeb::Storefront::JSFatal::SuperHero')
    csm.logError(error, '[DVWeb::Storefront] Error while trying to render a SuperHero')
    this.setState({ hasError: true })
  }

  handleMouseLeave = () => {
    this.resetInterval()
    const currentHero = this.currentHeroElement()
    if (!(currentHero &&
      currentHero.components.draperPlayerComponent &&
      currentHero.components.draperPlayerComponent.state.playing)) {
      this.autoScrollElements()
    }
  }

  handleMouseEnter = () => {
    this.resetInterval()
  }

  /* TODO: AVWebStorefront-718 - Integration Tests */
  initResizeAndTouchEvents() {
    this.autoScrollElements()
    if (!this.carouselContainer)
      return
    this.carouselContainer.addEventListener('mouseenter', this.handleMouseEnter)
    this.carouselContainer.addEventListener('mouseleave', this.handleMouseLeave)

    // This attaches to carousel element, not Container as above, to exclude the buttons from being in the "draggable zone" 
    if (!this.carouselElement)
      return
    this.carouselElement.addEventListener('touchstart', this.handleTouchStart)
    this.carouselElement.addEventListener('touchend', this.handleTouchEnd)
  }

  removeResizeAndTouchEvents() {
    this.carouselContainer && this.carouselContainer.removeEventListener('mouseenter', this.handleMouseEnter)
    this.carouselContainer && this.carouselContainer.removeEventListener('mouseleave', this.handleMouseLeave)
    this.carouselElement && this.carouselElement.removeEventListener('touchstart', this.handleTouchStart)
    this.carouselElement && this.carouselElement.removeEventListener('touchend', this.handleTouchEnd)
  }

  handleTouchStart = (evt: TouchEvent) => {
    this.touchXStart = evt.changedTouches[0].clientX
  }

  handleTouchEnd = (evt: TouchEvent) => {
    const DELTAX = 20
    const newTouchX = evt.changedTouches[0].clientX
    const isRTL = this.props.render && this.props.render.isRTL
    const directionRight = isRTL ? Direction.BACKWARD : Direction.FORWARD
    const directionLeft = isRTL ? Direction.FORWARD : Direction.BACKWARD
    if (Math.abs(newTouchX - this.touchXStart) > DELTAX) {
      if (evt.changedTouches[0].clientX > this.touchXStart)
        this.triggerInteractionScroll(directionLeft)
      else
        this.triggerInteractionScroll(directionRight)
    }
  }

  handleKeyDown = (evt: KeyboardEvent) => {
    const TAB_KEYCODE = 9
    const ENTER_KEYCODE = 13
    const prevIndexOffset = -1
    const nextIndexOffset = 1
    const currentIndex = this.state.index

    /*
   * @desc if the element is on focus we enable shortcuts in 2 ways:
   * 1. TAB key - if the tab is pressed go to the next slide unless you are
   * focusing on the last page. If you press the SHIFT key along with tab
   * go to the previous slide unless you are focusing on the firt page.
   * 2. ENTER key - if you press the enter key while focusing on the tab you will
   * be redirected to the link in the page.
   */
    if (evt.which === TAB_KEYCODE) {
      const nextIndex = currentIndex + (evt.shiftKey ? prevIndexOffset : nextIndexOffset)
      if ((evt.shiftKey && nextIndex >= 0) ||
        (!evt.shiftKey && nextIndex < this.props.items.length)) {
        evt.preventDefault()
        this.resetInterval()
        this.moveTo(nextIndex)
      }
    } else if (evt.which === ENTER_KEYCODE) {
      const currentItem = this.props.items[currentIndex]
      const targetUrl = currentItem && currentItem.link && currentItem.link.url
      if (targetUrl)
        window.location.href = targetUrl
    }
  }
  /* TODO: AVWebStorefront-718 - Integration Tests */
  /* istanbul ignore next */
  initTabEvents() {
    this.carouselContainer && this.carouselContainer.addEventListener('keydown', this.handleKeyDown)
  }

  removeTabEvents() {
    this.carouselContainer && this.carouselContainer.removeEventListener('keydown', this.handleKeyDown)
  }

  /* TODO: AVWebStorefront-718 - Integration Tests */
  /* istanbul ignore next */
  updateResize() {
    this.setState(this.recalculateState(this.state.index), () => {
      this.moveTo(this.state.index)
    })
  }

  shouldComponentUpdate(nextProps: SuperHeroProps) {
    if (nextProps.items.length !== this.props.items.length) {
      this.moveTo(0)
    }

    return true
  }

  componentDidUpdate(prevProps: SuperHeroProps) {
    if (prevProps.isFrozen && !this.props.isFrozen) {
      if (this.canPlayPreroll()) {
        this.playPreroll()
      } else {
        this.autoScrollElements()
      }
    } else if (!prevProps.isFrozen && this.props.isFrozen) {
      this.stopPreroll()
    }
  }

  resetInterval() {
    clearInterval(this.intervalID)
    this.intervalID = 0
  }

  canPlayPreroll() {
    const hasHeroComponent = (this.heroComponents &&
      this.heroComponents[`heroComponent-${this.state.index}`]),
      isPlaying = this.draperPlayerComponent && this.draperPlayerComponent.state.playing,
      hasPlayed = this.draperPlayerComponent && this.draperPlayerComponent.videoStarted
    return !this.props.isFrozen && hasHeroComponent && !isPlaying && !hasPlayed
  }

  playPreroll() {
    const currentHero = this.currentHeroElement()
    if (!(currentHero && currentHero.components && currentHero.components.draperPlayerComponent)) {
      return
    }
    if (this.intervalID) {
      this.resetInterval()
    }
    setTimeout(() => {
      const currentHero = this.currentHeroElement()
      !this.props.isFrozen && currentHero && currentHero.components && currentHero.components.draperPlayerComponent && currentHero.components
        .draperPlayerComponent.startPlayer(), PREROLL_ANIMATION_DELAY
    })
  }

  stopPreroll() {
    const currentHero = this.currentHeroElement()
    if (currentHero) {
      currentHero.components && currentHero.components.draperPlayerComponent && currentHero.components.draperPlayerComponent.stop()
    }
  }

  shouldNotAutoscroll() {
    return (this.props.isFrozen || this.state.loopCompleted ||
      (this.state.hasInteracted) || this.state.pausedForPlayback
    )
  }

  autoScrollElements() {
    if (this.shouldNotAutoscroll() || this.props.items[this.state.index].prerolls)
      return

    if (this.intervalID)
      return

    this.intervalID = window.setInterval(() => {
      let activeIndex = this.state.index

      if (this.state.index === this.props.items.length - 1) {
        activeIndex = NO_ACTIVE_INDEX
        this.setState({
          hasInteracted: true
        })
        this.resetInterval()
      }
      this.moveTo(activeIndex + 1)
    }, AUTOSCROLL_DELAY)
  }

  moveTo(scrollIndex: number) {
    if (this.props.items.length < 1)
      return
    const sanitisedIndex = realMod(scrollIndex, this.props.items.length)
    const isScrollingPastEnd = scrollIndex >= this.props.items.length

    if (isScrollingPastEnd)
      this.setState({ loopCompleted: true })

    this.stopPreroll()
    if (this.carouselElement) {
      const direction = this.props.render && this.props.render.isRTL ? Direction.FORWARD : Direction.BACKWARD
      this.carouselElement.style.transform = `translateX(${sanitisedIndex * 100 * direction}%)`
    }

    this.setState(
      this.recalculateState(sanitisedIndex),
      () => {
        if (this.canPlayPreroll() === true) {
          this.playPreroll()
        }
      }
    )
  }

  currentHeroElement() {
    return this.heroComponents[`heroComponent-${this.state.index}`]
  }

  triggerInteractionScroll(direction = Direction.FORWARD) {
    this.resetInterval()
    this.moveTo(this.state.index + direction)
    csm.count('SuperheroScroll')
  }


  itemsWithEnrichments() {
    if (!this.state.enrichments)
      return this.props.items
    const patchedItems = this.props.items.map(item => {
      const titleID = item.titleID
      const enrichment = titleID && this.state.enrichments[titleID]
      if (!enrichment)
        return item
      return {
        ...item,
        prerolls: enrichment.prerolls,
        watchlistAction: enrichment.watchlistAction,
        playbackAction: enrichment.playbackAction
      }
    })
    return patchedItems
  }

  isMinimumIndex() {
    return !this.state.index
  }

  isMaxmimumIndexHide() {
    return this.state.index === this.props.items.length - 1
  }

  isMaxmimumIndexDisable() {
    return this.state.index === this.props.items.length
  }

  hidePrev() {
    return this.props.render && this.props.render.isRTL ? this.isMaxmimumIndexHide() : this.isMinimumIndex()
  }

  disablePrev() {
    return this.props.render && this.props.render.isRTL ? this.isMaxmimumIndexDisable() : this.isMinimumIndex()
  }

  hideNext() {
    return this.props.render && this.props.render.isRTL ? this.isMinimumIndex() : this.isMaxmimumIndexHide()
  }

  disableNext() {
    return this.props.render && this.props.render.isRTL ? this.isMinimumIndex() : this.isMaxmimumIndexDisable()
  }

  render() {
    if (this.state.hasError)
      return <></>
    return (
      <div tabIndex={0} className={classNames(style.superhero, 'tst-superhero')}
        ref={(elem) => {
          this.carouselContainer = elem || undefined
        }}>
        <div className={style.container}>
          {this.state.isJavascriptEnabled && <button
            className={classNames({ [style.hidden]: this.hidePrev() },
              style.handle,
              style.leftHandle)}
            value="prev"
            onClick={() =>
              this.triggerInteractionScroll(this.props.render && this.props.render.isRTL ? Direction.FORWARD : Direction.BACKWARD)}
            disabled={this.disablePrev()}>
            <div className={style.icon}></div>
          </button>
          }
          <ul ref={(elem) => {
            this.carouselElement = elem || undefined
          }}
            className={classNames(
              'tst-superhero-item',
              style.animate,
              style.slider,
            )}>
            {this.itemsWithEnrichments().map((item, idx) => {
              const preroll = item.prerolls
              return <li key={item.image && item.image.url}>
                <HeroElement
                  loadNow={idx < 2}
                  item={item}
                  formFactor={this.props.formFactor}
                  featureSwitches={this.props.featureSwitches}
                  hasDraperPlayer={preroll && this.props.draperPlayerComponent !== null}
                  ref={(component) => {
                    if (component)
                      this.heroComponents[`heroComponent-${idx}`] = component
                  }}
                  index={idx}
                  autoScrollFunc={() => {
                    if (this.shouldNotAutoscroll()) {
                      return
                    }
                    setTimeout(() => {
                      this.moveTo(idx + 1)
                      this.autoScrollElements()
                    }, PREROLL_CONTINUE_SLIDER_DELAY)
                  }}
                  isActive={this.state.index === idx}
                />
              </li>
            })}
          </ul>
          {this.state.isJavascriptEnabled && <button ref={(elem) => {
            this.rightButton = elem || undefined
          }}
            value="next"
            className={classNames(
              { [style.hidden]: this.hideNext() },
              style.handle,
              style.rightHandle)}
            onClick={() =>
              this.triggerInteractionScroll(this.props.render && this.props.render.isRTL ? Direction.BACKWARD : Direction.FORWARD)}
            disabled={this.disableNext()}>
            <div className={style.icon}></div>
          </button>
          }
          {this.props.items.length > 1 &&
            <DotNav size={this.props.items.length}
              activeIndex={this.state.index}
              moveTo={(scrollIndex: number) => {
                this.moveTo(scrollIndex)
                csm.count('SuperheroScroll')
              }}
            />
          }
        </div>
      </div>
    )
  }
}

export default withArgs(SuperHeroComponent) as React.ComponentType<SuperHeroProps>
